/**
 * Read a peer bundle **locally**, so an operator can see what is in it before
 * anything is written (REQ-UI-150; feature-spec §2.2 / §5.2).
 *
 * Pure, React-free, and — this is the point — **entirely client-side**. Parsing
 * happens on the file the operator picked, in their browser, with no request at
 * all. Reading a bundle a partner site handed you must not be the same act as
 * installing it, and the BFF's import route has no dry-run: it unpacks and
 * installs. So the preview cannot be a server round trip, and making it one
 * would mean either a new endpoint or an install-then-report flow that writes
 * first and asks second.
 *
 * # The archive machinery is shared
 *
 * gzip inflation, tar framing, digest re-computation and ``${token}`` scanning
 * live in :file:`@/lib/bundle-archive` — peer and provider bundles are the same
 * format and differ only in which member carries the definition (CLAUDE.md
 * §4.4). What stays here is the *peer* reading of that definition. The
 * ``BundleUnreadable`` class and ``collectTokens`` are re-exported so this
 * module's public surface is unchanged by the extraction.
 *
 * # What the preview asserts, and what it does not
 *
 * It reports what the archive **contains** and re-computes each member's digest
 * against the manifest — an operator asking "is this the bundle they sent me?"
 * gets an answer here rather than after an install. It is **not** a second
 * validator: the BFF re-validates everything and is the authority (ADR-009
 * defence-in-depth), so this refuses only what it cannot render honestly, and
 * every rejection it does make is stated in the operator's words.
 *
 * Cross-references:
 *   - REQ-UI-150 (show what was parsed, block on unresolved placeholders).
 *   - REQ-PEER-103 (the install route); feature-spec D-8 (placeholders), D-15
 *     (the manifest digest is what an installer checks).
 *   - Flow W §5c (docs/engineering/detail/storyboards/flows/W-configure-peer-integration).
 */
import {
  BundleUnreadable,
  asRecord,
  asString,
  collectTokens,
  gunzip,
  readJsonMember,
  readManifest,
  untar,
  type BundleArtifact,
  type BundlePlaceholder,
} from "@/lib/bundle-archive";

export { BundleUnreadable, collectTokens };
export type { BundleArtifact };

/** A placeholder the bundle ships with, and whether the operator has answered it. */
export type BundlePreviewPlaceholder = BundlePlaceholder;

/** One connection, as the bundle's peer definition describes it. */
export interface BundlePreviewConnection {
  name: string;
  role: string;
  endpointHost: string | null;
  endpointPort: number | null;
  /** ``true`` when any field on this connection still carries a ``${token}``. */
  hasUnresolved: boolean;
}

/** One interface, as the bundle's peer definition describes it. */
export interface BundlePreviewInterface {
  name: string;
  typeId: string;
  transportSecurity: string | null;
  conversionMode: string | null;
  connections: BundlePreviewConnection[];
  /** ``true`` when a field on the interface itself (not a connection) is unresolved. */
  hasUnresolved: boolean;
}

/** Everything the import dialog renders before it writes anything. */
export interface BundlePreview {
  bundleName: string;
  generatedBy: string;
  peerName: string;
  peerDescription: string | null;
  interfaces: BundlePreviewInterface[];
  artifacts: BundleArtifact[];
  placeholders: BundlePreviewPlaceholder[];
  /** Distinct ``${token}`` names still present anywhere in the documents. */
  unresolvedTokens: string[];
  /**
   * Members whose bytes do not match the manifest, as operator-facing sentences.
   *
   * Separate from ``unresolvedTokens`` because they call for different acts: an
   * unresolved placeholder is the operator's to fill in, a digest mismatch means
   * the archive is not the one that was signed and is nobody's to fix locally.
   */
  digestProblems: string[];
}

function previewConnection(raw: unknown): BundlePreviewConnection {
  const record = asRecord(raw);
  return {
    name: asString(record.name) ?? "(unnamed)",
    role: asString(record.role) ?? "pull",
    endpointHost: asString(record.endpoint_host),
    endpointPort: typeof record.endpoint_port === "number" ? record.endpoint_port : null,
    hasUnresolved: collectTokens(record).length > 0,
  };
}

function previewInterface(raw: unknown): BundlePreviewInterface {
  const record = asRecord(raw);
  const connections = Array.isArray(record.connections)
    ? record.connections.map(previewConnection)
    : [];
  // Scoped to the interface's OWN fields: a token inside a connection is
  // reported on that connection's row, and counting it twice would tell an
  // operator there are more unanswered questions than the manifest asks.
  const { connections: _connections, ...own } = record;
  return {
    name: asString(record.name) ?? "(unnamed)",
    typeId: asString(record.type_id) ?? "(unknown)",
    transportSecurity: asString(record.transport_security),
    conversionMode: asString(record.conversion_mode),
    connections,
    hasUnresolved: collectTokens(own).length > 0,
  };
}

/**
 * Read a `.tar.gz` peer bundle into everything the import dialog shows.
 *
 * @param archive The bytes the operator selected.
 * @throws BundleUnreadable when the archive cannot be rendered honestly — not a
 *   gzip, no ``MANIFEST.json``, unparseable JSON. Every message is written for an
 *   operator, because this one is shown to them verbatim.
 */
export async function parsePeerBundle(archive: ArrayBuffer): Promise<BundlePreview> {
  const members = untar(await gunzip(archive));
  const manifest = asRecord(readJsonMember(members, "MANIFEST.json", "peer"));
  const definition = asRecord(readJsonMember(members, "peer.json", "peer"));
  const peer = asRecord(definition.peer);

  const unresolvedTokens = collectTokens(definition);
  const { bundleName, generatedBy, artifacts, placeholders, digestProblems } = await readManifest(
    manifest,
    members,
    unresolvedTokens,
  );

  return {
    bundleName,
    generatedBy,
    peerName: asString(peer.name) ?? "(unnamed peer)",
    peerDescription: asString(peer.description),
    interfaces: Array.isArray(peer.interfaces) ? peer.interfaces.map(previewInterface) : [],
    artifacts,
    placeholders,
    unresolvedTokens,
    digestProblems,
  };
}

/**
 * Whether this bundle may be installed from the dialog.
 *
 * **Unresolved placeholders block** (REQ-UI-150): installing with one persists
 * the literal ``${…}`` as configuration, and the interface then fails to
 * assemble far from the act that caused it. A digest problem blocks for a
 * different reason — the archive is not the one the manifest describes, and the
 * BFF will refuse it anyway.
 */
export function isInstallable(preview: BundlePreview): boolean {
  return preview.unresolvedTokens.length === 0 && preview.digestProblems.length === 0;
}
